import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Logo";
import { Button } from "./Button";
import { NAV_LINKS, scrollToId } from "../../data/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-kern-bg/80 backdrop-blur-xl border-b border-kern-taupe/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <button data-testid="nav-logo" onClick={() => go("#home")} className="shrink-0">
            <Wordmark />
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                onClick={() => go(l.href)}
                className="relative text-sm text-kern-dark/80 hover:text-kern-dark transition-colors duration-300 group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-kern-terracotta transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Button data-testid="nav-cta" onClick={() => go("#contact")} as="button" size="md">
              Maak een afspraak
            </Button>
          </div>

          <button
            data-testid="nav-hamburger"
            className="lg:hidden p-2 text-kern-dark"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[55] bg-kern-dark/30 backdrop-blur-sm lg:hidden"
              aria-hidden="true"
            />
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-[4.75rem] right-4 left-4 z-[60] lg:hidden bg-kern-bg rounded-3xl p-3 shadow-[0_24px_60px_rgba(79,80,72,0.22)] border border-kern-taupe/15"
            >
              <nav className="flex flex-col">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.href}
                    data-testid={`mobile-link-${l.href.slice(1)}`}
                    onClick={() => go(l.href)}
                    className="text-left font-serif text-xl text-kern-dark px-4 py-3.5 rounded-2xl hover:bg-kern-sage/15 transition-colors duration-200 border-b border-kern-taupe/10 last:border-0"
                  >
                    {l.label}
                  </button>
                ))}
              </nav>
              <Button
                as="button"
                data-testid="mobile-cta"
                onClick={() => go("#contact")}
                className="w-full justify-center mt-3"
              >
                Maak een afspraak
              </Button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
